<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useTimeAgo } from '@vueuse/core';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import MetricCard from './overview/MetricCard.vue';
import CaptainDisputesAPI from 'dashboard/api/captain/disputes';

const { t } = useI18n();
const route = useRoute();
const i18nKey = 'OVERVIEW_REPORTS.CHARGEBACKS';

const isLoading = ref(true);
const hasError = ref(false);
const lastUpdatedAt = ref(null);
const timeAgo = useTimeAgo(lastUpdatedAt);

const disputes = ref([]);

const activeStatuses = ['needs_response', 'under_review', 'open'];
const finalizedStatuses = ['won', 'lost'];

const activeDisputes = computed(() => {
  return disputes.value.filter(d => activeStatuses.includes(d.status));
});

const revenueAtRisk = computed(() => {
  const total = activeDisputes.value.reduce((sum, d) => {
    return sum + parseFloat(d.amount || 0);
  }, 0);
  return total;
});

const formattedRevenue = computed(() => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(revenueAtRisk.value);
});

const statusCounts = computed(() => {
  const counts = {
    needs_response: 0,
    under_review: 0,
    won: 0,
    lost: 0,
  };
  disputes.value.forEach(d => {
    const status = d.status;
    if (status === 'needs_response') counts.needs_response += 1;
    else if (status === 'under_review' || status === 'open')
      counts.under_review += 1;
    else if (status === 'won') counts.won += 1;
    else if (status === 'lost') counts.lost += 1;
  });
  return counts;
});

const winRate = computed(() => {
  const finalized = disputes.value.filter(d =>
    finalizedStatuses.includes(d.status)
  );
  if (finalized.length === 0) return null;
  const wins = finalized.filter(d => d.status === 'won').length;
  return Math.round((wins / finalized.length) * 100);
});

const winRateBarWidth = computed(() => {
  if (winRate.value === null) return 0;
  return Math.max(0, Math.min(100, winRate.value));
});

const disputesPageUrl = computed(() => {
  const accountId = route.params.accountId;
  return `/app/accounts/${accountId}/captain/2/disputes`;
});

const fetchDisputes = () => {
  isLoading.value = true;
  hasError.value = false;
  CaptainDisputesAPI.getSummary()
    .then(response => {
      disputes.value = response.data.disputes || [];
      lastUpdatedAt.value = new Date();
      hasError.value = false;
    })
    .catch(() => {
      hasError.value = true;
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const { startRefetching } = useLiveRefresh(fetchDisputes, 300000);

onMounted(() => {
  fetchDisputes();
  startRefetching();
});
</script>

<template>
  <div class="w-full">
    <MetricCard
      :header="t(`${i18nKey}.HEADER`)"
      :is-loading="isLoading"
      :loading-message="t(`${i18nKey}.LOADING_MESSAGE`)"
    >
      <template #header>
        <div class="flex items-center gap-2 flex-row">
          <div
            class="flex items-center justify-center w-6 h-6 rounded bg-n-ruby-3"
          >
            <fluent-icon icon="warning" size="14" class="text-n-ruby-11" />
          </div>
          <h5 class="mb-0 text-n-slate-12 font-medium text-lg">
            {{ t(`${i18nKey}.HEADER`) }}
          </h5>
          <span
            v-if="statusCounts.needs_response > 0"
            class="flex flex-row items-center py-0.5 px-2 rounded bg-n-ruby-3 text-xs"
          >
            <span class="bg-n-ruby-9 h-1 w-1 rounded-full mr-1" />
            <span class="text-xs text-n-ruby-11">
              {{
                t(`${i18nKey}.URGENT_COUNT`, {
                  count: statusCounts.needs_response,
                })
              }}
            </span>
          </span>
        </div>
        <div class="flex flex-row items-center justify-end gap-2">
          <router-link
            :to="disputesPageUrl"
            class="text-xs font-medium text-n-blue-text hover:underline"
          >
            {{ t(`${i18nKey}.VIEW_DETAILS`) }}
          </router-link>
        </div>
      </template>

      <!-- Error state -->
      <div v-if="hasError" class="w-full text-center py-4">
        <p class="text-sm text-n-slate-11">
          {{ t(`${i18nKey}.FETCH_ERROR`) }}
        </p>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="disputes.length === 0 && !isLoading"
        class="w-full text-center py-4"
      >
        <p class="text-sm text-n-slate-11">
          {{ t(`${i18nKey}.NO_DISPUTES`) }}
        </p>
      </div>

      <!-- Data display -->
      <div v-else class="flex flex-col w-full gap-4">
        <!-- Top row: Active count + Revenue at risk -->
        <div class="flex flex-wrap gap-6">
          <div class="flex-1 min-w-0">
            <h3 class="text-sm text-n-slate-11">
              {{ t(`${i18nKey}.ACTIVE_DISPUTES`) }}
            </h3>
            <p class="text-n-slate-12 text-3xl mb-0 mt-1 font-semibold">
              {{ activeDisputes.length }}
            </p>
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="text-sm text-n-slate-11">
              {{ t(`${i18nKey}.REVENUE_AT_RISK`) }}
            </h3>
            <p class="text-n-ruby-11 text-3xl mb-0 mt-1 font-semibold">
              {{ formattedRevenue }}
            </p>
          </div>
          <div v-if="winRate !== null" class="flex-1 min-w-0">
            <h3 class="text-sm text-n-slate-11">
              {{ t(`${i18nKey}.WIN_RATE`) }}
            </h3>
            <p class="text-n-slate-12 text-3xl mb-0 mt-1 font-semibold">
              {{ t(`${i18nKey}.WIN_RATE_VALUE`, { rate: winRate }) }}
            </p>
            <div class="mt-2 h-2 rounded-full bg-n-alpha-2 overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-500"
                :class="
                  winRate >= 50
                    ? 'bg-n-teal-9'
                    : winRate >= 30
                      ? 'bg-n-amber-9'
                      : 'bg-n-ruby-9'
                "
                :style="{ width: `${winRateBarWidth}%` }"
              />
            </div>
          </div>
        </div>

        <!-- Status breakdown -->
        <div
          class="flex flex-wrap items-center gap-4 pt-3 border-t border-n-container"
        >
          <div class="flex items-center gap-1.5">
            <span class="h-2 w-2 rounded-full bg-n-ruby-9" />
            <span class="text-sm text-n-slate-11">
              {{ statusCounts.needs_response }}
              {{ t(`${i18nKey}.NEEDS_RESPONSE`) }}
            </span>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="h-2 w-2 rounded-full bg-n-amber-9" />
            <span class="text-sm text-n-slate-11">
              {{ statusCounts.under_review }}
              {{ t(`${i18nKey}.UNDER_REVIEW`) }}
            </span>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="h-2 w-2 rounded-full bg-n-teal-9" />
            <span class="text-sm text-n-slate-11">
              {{ statusCounts.won }} {{ t(`${i18nKey}.WON`) }}
            </span>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="h-2 w-2 rounded-full bg-n-alpha-6" />
            <span class="text-sm text-n-slate-11">
              {{ statusCounts.lost }} {{ t(`${i18nKey}.LOST`) }}
            </span>
          </div>
        </div>

        <!-- Last updated -->
        <div
          v-if="lastUpdatedAt"
          class="flex items-center justify-end gap-1 pt-2 border-t border-n-container"
        >
          <fluent-icon icon="arrow-sync" size="12" class="text-n-slate-10" />
          <span class="text-xxs text-n-slate-10">
            {{ t(`${i18nKey}.UPDATED`, { time: timeAgo }) }}
          </span>
        </div>
      </div>
    </MetricCard>
  </div>
</template>
